import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, LoadingSpinner, ConfirmationModal, AccessColorFormModal } from '../components'
import { API_BASE_URL, LoadingSpinnerMeta, ConfirmationModalMeta, AccessColorFormModalMeta, } from '../data'
import { PlusIcon, ChevronLeftIcon, EditIcon, DeleteIcon } from '../icons'
import { AccessColorMetaProps, AccessColor, ColorCardProps } from '../data/types'
import { useResourcePermissions } from '../hooks/useResourcePermissions'

type Props = {
    meta: AccessColorMetaProps,
}


const ColorCard = ({ accessColor, onEdit, onDelete }: ColorCardProps) => {
    return (
        <div
            className="rounded-lg border border-gray-300 dark:border-gray-600
                 overflow-hidden hover:shadow-md transition p-3"
        >
            <div className='flex justify-between'>
                <div className="px-3 py-2 text-center bg-white dark:bg-gray-800">
                    <p className="text-lg font-medium text-gray-700 dark:text-gray-200">
                        {accessColor.color}
                    </p>
                </div>
                <div>
                    <Button variant='icon' onClick={() => onEdit(accessColor)}>
                        <EditIcon className='h4 w-4' />
                    </Button>
                    <Button variant='icon' color='danger' onClick={() => onDelete(accessColor)}>
                        <DeleteIcon className='h4 w-4' />
                    </Button>
                </div>
            </div>

            {/* Color swatch */}
            <div
                className="h-24 w-full"
                style={{ backgroundColor: accessColor.color }}
            />
        </div>
    );
};


export const AccessColors = (props: Props) => {
    const { meta } = props
    const { canEditAccessColor, canDeleteAccessColor, canCreateAccessColor } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [accessColors, setAccessColors] = useState<AccessColor[] | null>([])
    const [selectedAccessColor, setSelectedAccessColor] = useState<AccessColor | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isDeleteAccessColorModalOpen, setIsDeleteAccessColorModalOpen] = useState(false)
    const [isDeletingAccessColor, setIsDeletingAccessColor] = useState(false)
    const [isAccessColorModalOpen, setIsAccessColorModalOpen] = useState(false)


    useEffect(() => {
        const fetchAccessColors = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/access-color`, { credentials: 'include' })
                if (!response.ok) {
                    const text = await response.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const result = await response.json()
                setAccessColors(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching duties')
            }
            finally {
                setLoading(false)
            }
        }

        fetchAccessColors()

    }, [])

    const handleCreateAccessColor = async (accessColorData: any) => {
        const response = await fetch(`${API_BASE_URL}/access-color`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(accessColorData)
            })

        if (!response.ok) {
            const text = await response.text()
            const textObj = JSON.parse(text)
            throw new Error(`${textObj.message}`)
        }

        const result = await response.json()

        if (result.model) {
            setAccessColors(prev => [...prev, result.model])
        }
    }

    const handleUpdateAccessColor = async (accessColorData: AccessColor) => {
        if (!selectedAccessColor) return

        const response = await fetch(`${API_BASE_URL}/access-color/${selectedAccessColor.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(accessColorData)
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            const updatedAccessColor = result.model
            setAccessColors(prev =>
                prev.map(accessColor =>
                    accessColor.id === updatedAccessColor.id ? updatedAccessColor : accessColor
                )
            )
        }

    }

    const handleDeleteAccessColorConfirm = async () => {
        if (!selectedAccessColor) return

        setIsDeletingAccessColor(true)
        try {
            const response = await fetch(`${API_BASE_URL}/access-color/${selectedAccessColor.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            setAccessColors(prev => prev.filter(accessColor => accessColor.id !== selectedAccessColor.id));
            setIsDeleteAccessColorModalOpen(false)
            setSelectedAccessColor(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete Duty')
        } finally {
            setIsDeletingAccessColor(false)
        }
    }

    const handleEditAccessColorClick = (accessColor: AccessColor) => {
        setSelectedAccessColor(accessColor)
        setIsAccessColorModalOpen(true)
    }

    const handleCreateAccessColorClick = () => {
        setSelectedAccessColor(null)
        setIsAccessColorModalOpen(true)
    }


    const handleDeleteAccessColorClick = (accessColor: AccessColor) => {
        setSelectedAccessColor(accessColor)
        setIsDeleteAccessColorModalOpen(true)
    }

    const handleClose = () => {
        setSelectedAccessColor(null)
        setIsAccessColorModalOpen(false)
        setIsDeleteAccessColorModalOpen(false)
    }

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className="mb-10">


                        <div className="mb-3">
                            <Link
                                to="/admin"
                                className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                                <ChevronLeftIcon className="w-5 h-5 me-2" />
                                {meta.backButton}
                            </Link>
                        </div>

                        {/* Title + Add Button Row */}
                        <div className="flex items-center justify-between">

                            {/* Title */}
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                {meta.title}
                            </h1>

                            {/* Add Access Color Button */}
                            {canCreateAccessColor() && (
                                <Button
                                    onClick={handleCreateAccessColorClick}
                                    size="md"
                                    className="flex items-center"
                                >
                                    <PlusIcon className="w-5 h-5 me-2" />
                                    {meta.createButton}
                                </Button>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && accessColors.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noAnnouncementsMessage}</p>
                        </div>
                    )}

                    {!loading && !error && accessColors.length > 0 && (
                        <>
                            <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-end w-full">
                                {accessColors.map((ac, index) => (
                                    <ColorCard
                                        key={index}
                                        accessColor={ac}
                                        onEdit={canEditAccessColor(ac) ? handleEditAccessColorClick : undefined}
                                        onDelete={canDeleteAccessColor(ac) ? handleDeleteAccessColorClick : undefined} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <AccessColorFormModal
                meta={AccessColorFormModalMeta}
                isOpen={isAccessColorModalOpen}
                onClose={handleClose}
                onSubmit={selectedAccessColor ? handleUpdateAccessColor : handleCreateAccessColor}
                initialAccessColor={selectedAccessColor}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteAccessColorModalOpen}
                onCancel={handleClose}
                onConfirm={handleDeleteAccessColorConfirm}
                title={meta.onDeleteTitle}
                message={meta.onDeleteMessage}
                isLoading={isDeletingAccessColor}
            />


        </Fragment>
    )
}
